import { trackMpEvent } from '@/helpers/analyticsTS';
import { LoginSignupFlowInitiatedFrom, loginSignupFlowInitiatedFromSchema, Tracking } from '@/types/analytics';
import * as Sentry from '@sentry/react';
import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';

type Flow = Tracking.LoginSignupFlow['properties']['flow'];
type Step = Tracking.LoginSignupFlow['properties']['step'];

const sessionStateSchema = z.object({
  id: z.string().uuid(),
  initiated_from: loginSignupFlowInitiatedFromSchema,
  active: z.boolean(),
});

type State = z.infer<typeof sessionStateSchema>;

export interface LoginTrackingSessionMethods {
  start(initiated_from: LoginSignupFlowInitiatedFrom, options?: { force: boolean; id?: string }): State['id'];
  end(): void;
  isActive(): boolean;
  getId(): State['id'] | undefined;
  getInitiatedFrom(): LoginSignupFlowInitiatedFrom | undefined;
  track(props: { step: Step; flow: Flow }): void;
}

class LoginTrackingSession implements LoginTrackingSessionMethods {
  private state: State | null = null;

  start: LoginTrackingSessionMethods['start'] = (initiated_from, options) => {
    if (this.state?.active && !options?.force) {
      return this.state.id;
    }
    const id = options?.id ?? uuidv4();
    const state = { id, initiated_from, active: true };
    this.state = state;
    this.saveSessionInSessionStorage();
    return id;
  };

  end: LoginTrackingSessionMethods['end'] = () => {
    sessionStorage.removeItem('loginTrackingSession');
    if (!this.state?.active) return;
    this.state = null;
  };

  isActive: LoginTrackingSessionMethods['isActive'] = () => {
    return Boolean(this.state?.active) || Boolean(this.retrieveSessionFromSessionStorage()?.active);
  };

  getId: LoginTrackingSessionMethods['getId'] = () => {
    return this.state?.id;
  };

  getInitiatedFrom: LoginTrackingSessionMethods['getInitiatedFrom'] = () => {
    return this.state?.initiated_from;
  };

  track: LoginTrackingSessionMethods['track'] = (props) => {
    if (!this.isActive() || !this.state) {
      console.warn('[loginTracking] No active session — event skipped', props);
      Sentry.captureMessage(`[loginTracking] No active session for track() call step=${props.step} flow=${props.flow}`);
      return;
    }

    const { id, initiated_from } = this.state;

    const event: Tracking.LoginSignupFlow = {
      name: 'login_sign-up_flow',
      properties: {
        'login_sign-up_session_id': id,
        initiated_from,
        step: props.step,
        flow: props.flow,
      },
    };

    trackMpEvent(event);
  };

  private saveSessionInSessionStorage() {
    sessionStorage.setItem('loginTrackingSession', JSON.stringify(this.state));
  }

  private retrieveSessionFromSessionStorage() {
    const validate = sessionStateSchema.safeParse(JSON.parse(sessionStorage.getItem('loginTrackingSession') || '{}'));

    if (!validate.success) return;

    const state = {
      id: validate.data.id,
      initiated_from: validate.data.initiated_from,
      active: validate.data.active,
    };
    this.state = state;
    return state;
  }
}

// Singleton instance
export const loginTrackingSession = new LoginTrackingSession();
